/**
 * Cart Offer Engine — Popsigns Unified Upsell & Gift System
 *
 * Consolidates ALL cart-level offers and promotions:
 * 1. Size upgrades (12" Mini → 20" Classic) — inline per item
 * 2. Sides upgrades (Single → Double Sided) — inline per item
 * 3. Bundle suggestions (individual signs → bundles)
 * 4. Product cross-sells (popsigns → banners, flags, backdrops)
 * 5. Gift threshold — auto-add/remove gifts at spend thresholds
 * 6. Progress bar — gift-tier milestones (Best Day Ever cutout $150 + Carrying Case $300)
 *
 * Superseded the standalone gift-threshold-manager.js / gift-threshold-progress.js (removed 2026-06-18).
 * Gift config: THE single source of truth is the `giftConfig` object in layout/theme.liquid,
 * injected via window.CartOfferEngine.configureGifts(). No per-product-page override exists.
 *
 * Variant swaps use /cart/change.js + /cart/add.js to preserve
 * all line item properties (_pitchprint_*, _front_file_url, etc.)
 */
(() => {
  'use strict';

  // ==========================================
  // CONFIGURATION
  // ==========================================

  const CONFIG = {
    debug: false,

    // --- Upgrade offers ---
    sizeUpgrades: {
      '12" Mini Size': '20" Classic Size',
      '20x8" Mini Size': '36x10" Classic Size',
    },
    sidesUpgrades: {
      'Single Sided': 'Double Sided',
    },
    bundleSuggestions: [
      {
        trigger: { collection: 'popsigns' },
        bundles: [
          { handle: '5-pack-essentials-bundle', label: '5-Pack Essentials Bundle', minCartItems: 1, maxCartItems: 3 },
          { handle: '10-pack-bundle', label: '10-Pack Essentials Bundle', minCartItems: 3, maxCartItems: 99 },
        ]
      },
      {
        trigger: { collection: 'mothers-day-signs' },
        bundles: [
          { handle: 'mothers-fathers-day-bundle', label: "Mother's Day Bundle", minCartItems: 1, maxCartItems: 2 },
          { handle: 'flourish-popsigns-bundle', label: 'Flourish Bundle', minCartItems: 2, maxCartItems: 4 },
          { handle: '5-pack-essentials-bundle', label: '5-Pack Essentials Bundle', minCartItems: 4, maxCartItems: 99 },
        ]
      }
    ],
    crossSells: [
      // --- Collection-specific rules (evaluated first) ---
      {
        trigger: { collection: 'popsigns' },
        products: [
          { handle: 'custom-fabric-pillowcase-banner', label: 'Custom Fabric Banner', isCustom: true },
          { handle: 'custom-spirit-flags', label: 'Custom Spirit Flags', isCustom: true },
          { handle: 'custom-premium-feather-flags', label: 'Custom Feather Flag', isCustom: true },
          { handle: 'custom-cutout-signs', label: 'Custom Cutout Signs', isCustom: true },
        ],
        maxShow: 2
      },
      {
        trigger: { collection: 'fabric-backdrop' },
        products: [
          { handle: 'custom-popsigns', label: 'Custom Popsigns', isCustom: true },
          { handle: 'custom-spirit-flags', label: 'Custom Spirit Flags', isCustom: true },
          { handle: 'kids-photo-props', label: 'Kids Photo Props', isCustom: false, variantId: 48621464781077 },
        ],
        maxShow: 2
      },
      {
        trigger: { collection: 'premium-feather-flags' },
        products: [
          { handle: 'custom-popsigns', label: 'Custom Popsigns', isCustom: true },
          { handle: 'custom-fabric-pillowcase-banner', label: 'Custom Fabric Banner', isCustom: true },
        ],
        maxShow: 2
      },
      {
        trigger: { collection: 'church-signs' },
        products: [
          { handle: 'custom-fabric-pillowcase-banner', label: 'Custom Fabric Banner', isCustom: true },
          { handle: 'custom-premium-feather-flags', label: 'Custom Feather Flag', isCustom: true },
          { handle: 'kids-photo-props', label: 'Kids Photo Props', isCustom: false, variantId: 48621464781077 },
        ],
        maxShow: 3
      },
      {
        trigger: { collection: 'wedding-signs' },
        products: [
          { handle: 'custom-fabric-pillowcase-backdrop', label: 'Custom Fabric Backdrop', isCustom: true },
          { handle: 'custom-fabric-pillowcase-banner', label: 'Custom Fabric Banner', isCustom: true },
          { handle: 'custom-cutout-signs', label: 'Custom Cutout Signs', isCustom: true },
        ],
        maxShow: 2
      },
      {
        trigger: { collection: 'graduation-signs' },
        products: [
          { handle: 'custom-fabric-pillowcase-banner', label: 'Custom Fabric Banner', isCustom: true },
          { handle: 'custom-cutout-signs', label: 'Custom Cutout Signs', isCustom: true },
          { handle: 'kids-photo-props', label: 'Kids Photo Props', isCustom: false, variantId: 48621464781077 },
        ],
        maxShow: 2
      },
      {
        trigger: { collection: 'school-signs' },
        products: [
          { handle: 'custom-premium-feather-flags', label: 'Custom Feather Flag', isCustom: true },
          { handle: 'kids-photo-props', label: 'Kids Photo Props', isCustom: false, variantId: 48621464781077 },
          { handle: 'custom-cutout-signs', label: 'Custom Cutout Signs', isCustom: true },
        ],
        maxShow: 2
      },
      // --- Popular fallback (matches any cart) ---
      {
        trigger: { any: true },
        products: [
          { handle: 'custom-popsigns', label: 'Custom Popsigns', isCustom: true },
          { handle: 'custom-fabric-pillowcase-banner', label: 'Custom Fabric Banner', isCustom: true },
          { handle: 'custom-spirit-flags', label: 'Custom Spirit Flags', isCustom: true },
          { handle: 'kids-photo-props', label: 'Kids Photo Props', isCustom: false, variantId: 48621464781077 },
          { handle: 'custom-cutout-signs', label: 'Custom Cutout Signs', isCustom: true },
          { handle: 'custom-premium-feather-flags', label: 'Custom Feather Flag', isCustom: true },
        ],
        maxShow: 3
      },
    ],
    excludeHandles: ['free-gift-product', 'production-speed-upgrade'],
    productCacheTTL: 5 * 60 * 1000,

    // --- Social proof & rationale ---
    socialProof: {
      sides_upgrade: { pct: '84%', label: 'Add This' },
      size_upgrade: { pct: '79%', label: 'Add This' },
    },
    offerRationale: {
      sides_upgrade: 'Visible from both directions \u2014 most customers choose this',
      size_upgrade: '2x the visibility for events and entrances',
    },

    // --- Enhanced cart item layout (bubble badges) ---
    // Set to false to revert to original text-row layout
    enhancedLayout: true,

    // --- Gift threshold ---
    gift: {
      enabled: false,
      thresholds: [],       // [{amount: 300, variantId: 123, title: "Free Gift", notificationMessage: "..."}]
      giftPropertyKey: '_auto_gift',
      showNotifications: true,
      autoRemoveBelow: true,
      showProgressBar: true,
      shippingThreshold: 150,  // dollars — LOWER gift-tier milestone ($150 Best Day Ever); free-shipping milestone retired. theme.liquid giftConfig overrides at runtime.
    },

    // --- Production speed ---
    productionSpeed: {
      enabled: true,
      handle: 'production-speed-upgrade',
      propertyKey: '_processing_fee',
      attributeKey: 'production_speed',
      rushMultiplier: 0.10,
      rushMinimum: 1500,   // $15 minimum
      expressMultiplier: 0.20,
      expressMinimum: 2500, // $25 minimum
      rushVariants: [
        { id: 49818896662805, price: 500 },
        { id: 49818896695573, price: 1000 },
        { id: 49818896728341, price: 1500 },
        { id: 49818896761109, price: 2000 },
        { id: 49818896793877, price: 2500 },
        { id: 49818896826645, price: 3000 },
        { id: 49818896859413, price: 3500 },
        { id: 49818896892181, price: 4000 },
        { id: 49818896924949, price: 5000 },
        { id: 49818896957717, price: 6000 },
        { id: 49818896990485, price: 7500 },
        { id: 49819003683093, price: 8000 },
        { id: 49819003715861, price: 9000 },
        { id: 49818897023253, price: 10000 },
        { id: 49818897056021, price: 12500 },
        { id: 49818897088789, price: 15000 },
        { id: 49819003814165, price: 17500 },
        { id: 49819003846933, price: 20000 },
      ],
      expressVariants: [
        { id: 49818897121557, price: 500 },
        { id: 49818897154325, price: 1000 },
        { id: 49818897187093, price: 1500 },
        { id: 49818897219861, price: 2000 },
        { id: 49818897252629, price: 2500 },
        { id: 49818897285397, price: 3000 },
        { id: 49818897318165, price: 3500 },
        { id: 49818897350933, price: 4000 },
        { id: 49818897383701, price: 5000 },
        { id: 49818897416469, price: 6000 },
        { id: 49818897449237, price: 7500 },
        { id: 49819003748629, price: 8000 },
        { id: 49819003781397, price: 9000 },
        { id: 49818897482005, price: 10000 },
        { id: 49818897514773, price: 12500 },
        { id: 49818897547541, price: 15000 },
        { id: 49819003879701, price: 17500 },
        { id: 49819003912469, price: 20000 },
        { id: 49819003945237, price: 25000 },
        { id: 49819003978005, price: 30000 },
      ],
      tiers: {
        standard: { label: 'Standard', timeline: '2-4 days', fire: '' },
        rush: { label: 'Rush', timeline: '1-2 days', fire: '\uD83D\uDD25' },
        express: { label: 'Express', timeline: 'Next Day', fire: '\uD83D\uDD25\uD83D\uDD25' },
      },
    },
  };

  const log = (...args) => CONFIG.debug && console.log('[CartOffers]', ...args);

  function formatPriceDiff(cents) {
    const d = cents / 100;
    return '+$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }

  // ==========================================
  // STATE
  // ==========================================

  let isEvaluating = false;
  let giftProcessing = false;
  let lastGiftCheck = 0;
  const GIFT_CHECK_INTERVAL = 1000;
  let currentSpeedSelection = 'standard';
  let speedProcessing = false;

  // ==========================================
  // PRODUCT DATA CACHE
  // ==========================================

  const productCache = new Map();

  async function getProduct(handle) {
    const cached = productCache.get(handle);
    if (cached && Date.now() - cached.ts < CONFIG.productCacheTTL) {
      return cached.data;
    }
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      productCache.set(handle, { data, ts: Date.now() });
      return data;
    } catch (e) {
      log('Failed to fetch product:', handle, e);
      return null;
    }
  }

  async function getCart() {
    try {
      const res = await fetch('/cart.js');
      return await res.json();
    } catch (e) {
      log('Failed to fetch cart:', e);
      return null;
    }
  }

  // ==========================================
  // CART ITEM METADATA (Liquid-injected)
  // ==========================================

  /**
   * Read Liquid-injected cart item metadata (collections, metafields).
   * Returns a Map of handle → { collections: string[], type, usesPitchprint, ... }
   */
  function getCartItemMetadata() {
    const map = new Map();
    const el = document.querySelector('[data-cart-item-metadata]');
    if (!el) return map;
    try {
      const data = JSON.parse(el.textContent.trim());
      for (const [handle, meta] of Object.entries(data)) {
        map.set(handle, meta);
      }
    } catch (e) {
      log('Failed to parse cart item metadata:', e);
    }
    return map;
  }

  // ==========================================
  // GIFT THRESHOLD LOGIC
  // ==========================================

  /**
   * Calculate cart total excluding auto-gift items (in cents).
   */
  function getCartTotalExcludingGifts(cart) {
    const giftItems = cart.items.filter(item =>
      item.properties && item.properties[CONFIG.gift.giftPropertyKey]
    );
    if (giftItems.length === 0) return cart.total_price;
    const giftTotal = giftItems.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
    return cart.total_price - giftTotal;
  }

  /**
   * Scope gate: a threshold with `requiresCartProperty` only counts as met when the
   * cart contains a non-gift line carrying that property (e.g. '_wtk_signs' = a Welcome
   * Kit). No property on the threshold = unscoped (any cart, original behavior).
   */
  function cartHasRequiredItem(cart, threshold) {
    if (!threshold.requiresCartProperty) return true;
    return cart.items.some(item =>
      item.properties &&
      item.properties[threshold.requiresCartProperty] != null &&
      !item.properties[CONFIG.gift.giftPropertyKey]
    );
  }

  /**
   * Check gift thresholds and add/remove gifts as needed.
   */
  async function checkGiftThreshold(cart) {
    if (!CONFIG.gift.enabled || CONFIG.gift.thresholds.length === 0) return;

    const now = Date.now();
    if (now - lastGiftCheck < GIFT_CHECK_INTERVAL) return;
    if (giftProcessing) return;
    giftProcessing = true;
    lastGiftCheck = now;

    try {
      const totalCents = getCartTotalExcludingGifts(cart);

      // Eligible = dollar amount met AND cart-scope satisfied. On equal amount, prefer the MORE
      // SPECIFIC threshold (one with requiresCartProperty) — so a Welcome-Kit cart gets the kit
      // gift (carrying case) while any other $300+ cart falls through to the global default gift.
      // handleThresholdMet + removeOtherTierGifts then swap out the wrong gift if one is present.
      const metThreshold = CONFIG.gift.thresholds
        .filter(t => totalCents >= t.amount * 100 && cartHasRequiredItem(cart, t))
        .sort((a, b) => (b.amount - a.amount) || ((b.requiresCartProperty ? 1 : 0) - (a.requiresCartProperty ? 1 : 0)))[0];

      if (metThreshold) {
        await handleThresholdMet(cart, metThreshold);
      } else {
        await handleBelowThreshold(cart);
      }
    } catch (e) {
      log('Gift threshold error:', e);
    } finally {
      giftProcessing = false;
    }
  }

  async function handleThresholdMet(cart, threshold) {
    log('Threshold met:', threshold.amount, threshold.title);

    const giftInCart = cart.items.find(item =>
      item.variant_id === threshold.variantId ||
      (item.properties && item.properties[CONFIG.gift.giftPropertyKey] === String(threshold.variantId))
    );

    if (giftInCart) {
      // Remove other tier gifts
      await removeOtherTierGifts(cart, threshold.variantId);
      return;
    }

    await addGiftToCart(threshold);
  }

  async function handleBelowThreshold(cart) {
    if (!CONFIG.gift.autoRemoveBelow) return;

    const giftItems = cart.items.filter(item =>
      item.properties && item.properties[CONFIG.gift.giftPropertyKey]
    );

    if (giftItems.length > 0) {
      log('Removing', giftItems.length, 'gift items (below threshold)');
      for (const giftItem of giftItems) {
        const lineIndex = cart.items.findIndex(item => item.key === giftItem.key);
        if (lineIndex !== -1) {
          await removeCartLine(lineIndex + 1);
        }
      }
    }
  }

  async function removeOtherTierGifts(cart, currentGiftVariantId) {
    const otherGifts = cart.items.filter(item => {
      if (!item.properties || !item.properties[CONFIG.gift.giftPropertyKey]) return false;
      return item.variant_id !== currentGiftVariantId;
    });

    for (const gift of otherGifts) {
      const lineIndex = cart.items.findIndex(item => item.key === gift.key);
      if (lineIndex !== -1) {
        await removeCartLine(lineIndex + 1);
      }
    }
  }

  async function addGiftToCart(threshold) {
    try {
      log('Adding gift:', threshold.title, '(variant:', threshold.variantId, ')');

      const formData = new FormData();
      formData.append('items[0][id]', threshold.variantId);
      formData.append('items[0][quantity]', '1');
      formData.append(`items[0][properties][${CONFIG.gift.giftPropertyKey}]`, String(threshold.variantId));
      formData.append('items[0][properties][Gift]', 'Free Gift');

      if (threshold.customMessage) {
        formData.append('items[0][properties][_gift_message]', threshold.customMessage);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to add gift: ' + response.status + ' - ' + errorText);
      }

      log('Gift added successfully');

      // Refresh cart sections inline (no page reload)
      await refreshCartSections();
    } catch (e) {
      log('Error adding gift:', e);
    }
  }

  async function removeCartLine(lineNumber) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ line: lineNumber, quantity: 0 })
      });

      if (!response.ok) throw new Error('Failed to remove line: ' + response.status);
      log('Line', lineNumber, 'removed');

      // Refresh cart sections inline (no page reload)
      await refreshCartSections();
    } catch (e) {
      log('Error removing cart line:', e);
    }
  }

  // Remove a line by its STABLE line-item key (item.key), not by line index.
  // Index-based removal is unsafe in the drawer: a line index captured at click time
  // goes stale after any cart re-render (the collapse-animation delay, or a second
  // rapid remove shifting indices), so /cart/change targets the wrong/missing line and
  // no-ops — Shopify returns an unchanged item_count and cart.js then surfaces a bogus
  // "You can only add 1 of this item to your cart." quantity error. The key is stable
  // and independent per line, so concurrent removes can't collide.
  async function removeCartLineByKey(key) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      if (!response.ok) throw new Error('Failed to remove line: ' + response.status);
      log('Line key', key, 'removed');
      await refreshCartSections();
    } catch (e) {
      log('Error removing cart line by key:', e);
      // Last-resort fallback — navigate to the real cart so the user isn't stuck.
      window.location.href = '/cart';
    }
  }

  // Single delegated, capture-phase handler for the drawer's enhanced remove control.
  // Delegation (vs the old per-item binding in hydrateEnhancedItem) means a click is
  // ALWAYS intercepted — even on a freshly re-rendered <li> that the 500ms-debounced
  // re-init hasn't hydrated yet. Without it, the bare <a href="{{ item.url_to_remove }}">
  // does a native navigation to /cart. Capture phase + stopPropagation also beats any
  // prefetch / instant-nav handler on the link.
  let _removeDelegationBound = false;
  function bindRemoveDelegation() {
    if (_removeDelegationBound) return;
    _removeDelegationBound = true;

    document.addEventListener('click', (e) => {
      const trash = e.target.closest('[data-enhanced-remove]');
      if (!trash) return;
      e.preventDefault();
      e.stopPropagation();

      const li = trash.closest('li[data-enhanced]') || trash.closest('li');
      if (li && li.classList.contains('cart-item--removing')) return; // guard double-click

      const key = (li && li.dataset.lineKey) || trash.dataset.lineKey;
      const fire = () => {
        if (key) {
          removeCartLineByKey(key);
        } else {
          // No stable key (stale cached markup) — re-read the index from the live DOM
          // right before firing so it isn't stale, then use the native cart-items path.
          const cartItems = li ? li.closest('cart-items') : null;
          const idx = trash.dataset.index;
          if (cartItems && typeof cartItems.updateQuantity === 'function' && idx) {
            cartItems.updateQuantity(idx, 0);
          } else {
            window.location.href = trash.getAttribute('href') || '/cart';
          }
        }
      };

      // Collapse the line first (the collapse IS the feedback; .cart-item--removing
      // sets pointer-events:none). Reduced motion → remove immediately.
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (li) li.classList.add('cart-item--removing');
      setTimeout(fire, reduce ? 0 : 400);
    }, true); // capture phase
  }

  // ==========================================
  // PROGRESS BAR
  // ==========================================

  // Last-known threshold met-state across calls. Module-level (not DOM-based) so the
  // one-time "just unlocked" marker pulse fires on a real session crossing and isn't
  // suppressed by refreshCartSections() re-rendering the milestone already-complete.
  let _prevShippingMet = null;
  let _prevGiftMet = null;
  // Last fill % so a section re-render can pre-seed the new (freshly-injected) fill element
  // to its prior width before first paint → the bar glides old→new instead of snapping.
  let _prevFillPercent = null;

  // Fire the circle-pop + ring-pulse once on a milestone that was just hit.
  function pulseMilestone(el) {
    if (!el) return;
    el.classList.remove('milestone--just-completed');
    void el.offsetWidth; // restart the animation if it's mid-flight
    el.classList.add('milestone--just-completed');
    setTimeout(() => el.classList.remove('milestone--just-completed'), 600);
  }

  // Animate summary values whose RENDERED TEXT changed since last check (count roll, subtotal/
  // total/savings flash). Text-string comparison (no money parsing) keyed in _prevSummaryText.
  // Idempotent: called both from the synchronous cartUpdate hook (pre-paint, fires in sync with
  // the theme's re-render) AND from init() (engine refresh paths) — whichever sees the change
  // first animates + records it; the other no-ops. First call just seeds (no animation).
  // Per-line price flash: when a drawer line's rendered price text changes (e.g. qty change),
  // flash it green→back. Keyed by variant id + occurrence (de-dupes same-variant lines); the
  // map is rebuilt each call so removed lines drop out and don't spuriously flash on re-add.
  let _prevLinePriceText = {};
  function flashChangedLinePrices() {
    const next = {};
    const seen = {};
    document.querySelectorAll('.mini-cart__navigation li[data-variant-id]').forEach((li) => {
      const vid = li.getAttribute('data-variant-id');
      const priceEl = li.querySelector('.enhanced-price-top');
      if (!vid || !priceEl) return;
      const key = vid + '#' + (seen[vid] = (seen[vid] || 0) + 1);
      const txt = (priceEl.textContent || '').trim();
      if (_prevLinePriceText[key] != null && _prevLinePriceText[key] !== txt) {
        priceEl.classList.remove('ps-price-flash');
        void priceEl.offsetWidth;
        priceEl.classList.add('ps-price-flash');
      }
      next[key] = txt;
    });
    _prevLinePriceText = next;
  }

  const _prevSummaryText = {};
  function flashChangedSummaryValues() {
    const targets = [
      { sel: '.mini-cart__count', key: 'count', cls: 'ps-count-roll' },
      { sel: '#mini-cart-subtotal', key: 'subtotal', cls: 'ps-value-flash' },
      { sel: '.cart-order-summary__row--total .cart-order-summary__value', key: 'total', cls: 'ps-value-flash' },
      { sel: '.cart-order-summary__savings-value', key: 'savings', cls: 'ps-savings-flash' },
    ];
    targets.forEach((t) => {
      const el = document.querySelector(t.sel);
      const txt = el ? (el.textContent || '').trim() : null;
      if (el && _prevSummaryText[t.key] != null && _prevSummaryText[t.key] !== txt) {
        el.classList.remove(t.cls);
        void el.offsetWidth; // restart on the (possibly reused) element
        el.classList.add(t.cls);
      }
      _prevSummaryText[t.key] = txt;
    });
  }

  // Pin every progress fill to its PREVIOUS width (transition off) so the next
  // updateProgressBar() animates old→new. Must run synchronously and BEFORE the browser
  // paints: subscribed to PUB_SUB cartUpdate, which the theme publishes in the same task
  // right after cart-drawer.js renderContents() injects the new section (with the new
  // server width) — otherwise that paint shows as an instant jump and only the float-vs-
  // Liquid rounding remainder glides. Also called from refreshCartSections (engine path).
  function preSeedProgressFill(root) {
    if (_prevFillPercent === null) return;
    (root || document).querySelectorAll('.combined-threshold-progress__fill').forEach(f => {
      f.style.transition = 'none';
      f.style.width = _prevFillPercent + '%';
    });
  }

  /**
   * Render/update the combined shipping + gift threshold progress bar.
   * Finds all [data-combined-threshold-progress] containers in the DOM
   * (cart page + mini-cart) and updates them.
   */
  function updateProgressBar(cart) {
    const containers = document.querySelectorAll('[data-combined-threshold-progress]');
    if (containers.length === 0) return;

    if (!CONFIG.gift.showProgressBar) {
      containers.forEach(c => { c.style.visibility = 'hidden'; c.style.display = 'none'; });
      return;
    }
    if (!CONFIG.gift.enabled) {
      // Gift not configured yet (theme.liquid configureGifts runs shortly after load).
      // Leave the server-rendered (Liquid-first) bar visible — hiding it here caused the
      // bar to flash off on drawer-open / cart re-render until JS re-revealed it. GAP 2.
      return;
    }

    const totalCents = getCartTotalExcludingGifts(cart);
    const totalDollars = totalCents / 100;

    const shippingThreshold = CONFIG.gift.shippingThreshold;
    const giftThreshold = CONFIG.gift.thresholds.length > 0
      ? Math.max(...CONFIG.gift.thresholds.map(t => t.amount))
      : 300;
    const progressPercent = Math.min(100, (totalDollars / giftThreshold) * 100);
    const shippingMet = totalDollars >= shippingThreshold;
    const giftMet = totalDollars >= giftThreshold;

    // Detect a fresh threshold crossing (false→true) for the one-time marker pulse.
    // First call has _prev === null, so `null === false` is false → no pulse on load.
    const shipJustHit = _prevShippingMet === false && shippingMet;
    const giftJustHit = _prevGiftMet === false && giftMet;
    _prevShippingMet = shippingMet;
    _prevGiftMet = giftMet;

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cart.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    // Build message: show the NEXT gift milestone to achieve.
    // Tier 1 ($150) = Best Day Ever cutout; Tier 2 ($300) = carrying case.
    // (The free-shipping milestone was retired when shipping moved to tiered/flat rates;
    //  the `shipping*` variable/class names are kept = the LOWER ($100) gift tier.)
    const GIFT_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="currentColor"><path d="M216,72H180.92c.39-.33.79-.65,1.17-1A29.53,29.53,0,0,0,192,49.57,32.62,32.62,0,0,0,158.44,16,29.53,29.53,0,0,0,137,25.91a54.94,54.94,0,0,0-9,14.48,54.94,54.94,0,0,0-9-14.48A29.53,29.53,0,0,0,97.56,16,32.62,32.62,0,0,0,64,49.57,29.53,29.53,0,0,0,73.91,71c.38.33.78.65,1.17,1H40A16,16,0,0,0,24,88v32a16,16,0,0,0,16,16v64a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V136a16,16,0,0,0,16-16V88A16,16,0,0,0,216,72ZM149,36.51a13.69,13.69,0,0,1,10-4.5h.49A16.62,16.62,0,0,1,176,49.08a13.69,13.69,0,0,1-4.5,10c-9.49,8.4-25.24,11.36-35,12.4C137.7,60.89,141,45.5,149,36.51Zm-64.09.36A16.63,16.63,0,0,1,96.59,32h.49a13.69,13.69,0,0,1,10,4.5c8.39,9.48,11.35,25.2,12.39,34.92-9.72-1-25.44-4-34.92-12.39a13.69,13.69,0,0,1-4.5-10A16.6,16.6,0,0,1,84.87,36.87ZM40,88h80v32H40Zm16,48h64v64H56Zm144,64H136V136h64Zm16-80H136V88h80v32Z"/></svg>';
    let messageHTML = '';
    if (giftMet) {
      messageHTML = '<span class="threshold-unlocked">' + GIFT_SVG + ' Free Carrying Case unlocked!</span>';
    } else if (shippingMet) {
      const remaining = giftThreshold - totalDollars;
      messageHTML = '<span class="threshold-unlocked threshold-unlocked--shipping">' + GIFT_SVG + ' Best Day Ever cutout unlocked</span>'
        + '<div class="threshold-next"><span class="price"><bdi>' + formatter.format(remaining) + '</bdi></span> away from a free carrying case</div>';
    } else {
      const remaining = shippingThreshold - totalDollars;
      messageHTML = '<span class="threshold-unlocked threshold-unlocked--pending">' + GIFT_SVG + ' Spend <span class="price"><bdi>' + formatter.format(remaining) + '</bdi></span> more for a <strong>free Best Day Ever cutout!</strong></span>';
    }

    containers.forEach(container => {
      const textSpan = container.querySelector('.combined-threshold-progress__text');
      const progressFill = container.querySelector('.combined-threshold-progress__fill');
      const shippingMilestone = container.querySelector('[data-milestone="shipping"]');
      const giftMilestone = container.querySelector('[data-milestone="gift"]');
      if (!textSpan || !progressFill || !shippingMilestone || !giftMilestone) return;

      // Update milestone classes
      shippingMilestone.className = 'milestone milestone--shipping' +
        (shippingMet ? ' milestone--complete' : ' milestone--active');
      giftMilestone.className = 'milestone milestone--gift' +
        (giftMet ? ' milestone--complete' : (shippingMet ? ' milestone--active' : ''));

      // One-time pop + ring pulse on a freshly-crossed threshold.
      if (shipJustHit) pulseMilestone(shippingMilestone);
      if (giftJustHit) pulseMilestone(giftMilestone);

      if (giftMet) {
        container.classList.add('combined-threshold-progress--success');
      } else {
        container.classList.remove('combined-threshold-progress--success');
      }

      // Re-enable the CSS width transition (refreshCartSections may have pinned it to
      // 'none' while pre-seeding the old width) then set the new width → glides old→new.
      progressFill.style.transition = '';
      progressFill.style.width = progressPercent + '%';
      textSpan.innerHTML = messageHTML;
      container.style.visibility = 'visible';
      // Clear (don't set 'block') so the CSS `display:flex` + order rules hold — the lean layout
      // puts the helper text BELOW the bar via flex order, which an inline display:block would
      // override (reverting to source order = text above the bar). PARITY-GAPS GAP 2 follow-up.
      container.style.display = '';
    });

    _prevFillPercent = progressPercent;
  }

  // ==========================================
  // PRODUCTION SPEED LOGIC
  // ==========================================

  /**
   * Calculate cart subtotal excluding gift items AND speed fee items (cents).
   * This is the "base" subtotal that surcharges are calculated from.
   */
  function getCartSubtotalExcludingFees(cart) {
    let total = 0;
    for (const item of cart.items) {
      if (item.properties && item.properties[CONFIG.gift.giftPropertyKey]) continue;
      if (item.properties && item.properties[CONFIG.productionSpeed.propertyKey]) continue;
      total += item.final_line_price || 0;
    }
    return total;
  }

  /**
   * Find the variant whose price is >= target (nearest above).
   * If none is >=, use the highest available.
   */
  function findBestVariant(variants, targetCents) {
    if (!variants || variants.length === 0) return null;
    // variants are sorted ascending by price
    for (const v of variants) {
      if (v.price >= targetCents) return v;
    }
    // All variants are below target — use the highest
    return variants[variants.length - 1];
  }

  /**
   * Find any existing speed line item in cart.
   */
  function getCurrentSpeedItem(cart) {
    return cart.items.find(item =>
      item.properties && item.properties[CONFIG.productionSpeed.propertyKey]
    ) || null;
  }

  /**
   * Apply a speed selection: add, swap, or remove the speed line item.
   */
  async function applySpeedSelection(speed, cart) {
    if (speedProcessing) return;
    speedProcessing = true;
    const cfg = CONFIG.productionSpeed;

    try {
      const existingItem = getCurrentSpeedItem(cart);
      const subtotal = getCartSubtotalExcludingFees(cart);

      if (speed === 'standard') {
        // Remove speed item if one exists
        if (existingItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: existingItem.key, quantity: 0 }),
          });
        }
        // Clear cart attribute
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { [cfg.attributeKey]: '' } }),
        });
        currentSpeedSelection = 'standard';
        await refreshCartSections();
        return;
      }

      // Rush or Express
      const multiplier = speed === 'rush' ? cfg.rushMultiplier : cfg.expressMultiplier;
      const minimum = speed === 'rush' ? (cfg.rushMinimum || 0) : (cfg.expressMinimum || 0);
      const variants = speed === 'rush' ? cfg.rushVariants : cfg.expressVariants;
      const surcharge = Math.max(minimum, Math.round(subtotal * multiplier));
      const bestVariant = findBestVariant(variants, surcharge);

      if (!bestVariant) {
        log('No variant found for speed:', speed, surcharge);
        return;
      }

      if (existingItem) {
        // Already have a speed item — swap to correct variant if needed
        if (existingItem.variant_id === bestVariant.id) {
          // Same variant, just update attribute
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { [cfg.attributeKey]: speed } }),
          });
          currentSpeedSelection = speed;
          await refreshCartSections();
          return;
        }
        // Remove old, add new
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: existingItem.key, quantity: 0 }),
        });
      }

      // Add new speed line item
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: bestVariant.id,
            quantity: 1,
            properties: {
              [cfg.propertyKey]: speed,
              _hidden: 'true',
            },
          }],
        }),
      });

      // Set cart attribute for order admin visibility
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [cfg.attributeKey]: speed } }),
      });

      currentSpeedSelection = speed;
      await refreshCartSections();
    } catch (e) {
      log('Speed selection error:', e);
      await refreshCartSections();
    } finally {
      speedProcessing = false;
    }
  }

  /**
   * Sync speed item on cart change — recalculate surcharge and swap variant if needed.
   * Also auto-removes speed item if cart is empty.
   */
  async function syncSpeedOnCartChange(cart) {
    if (!CONFIG.productionSpeed.enabled) return;
    if (speedProcessing) return;

    const existingItem = getCurrentSpeedItem(cart);
    if (!existingItem) {
      // Detect speed from cart attribute (e.g., after page reload)
      const attr = cart.attributes && cart.attributes[CONFIG.productionSpeed.attributeKey];
      if (attr && (attr === 'rush' || attr === 'express')) {
        currentSpeedSelection = attr;
      }
      return;
    }

    // Detect current speed from the line item property
    const speedType = existingItem.properties[CONFIG.productionSpeed.propertyKey];
    currentSpeedSelection = speedType;

    const subtotal = getCartSubtotalExcludingFees(cart);

    // Auto-remove if no real items left
    const realItems = cart.items.filter(item =>
      !(item.properties && item.properties[CONFIG.gift.giftPropertyKey]) &&
      !(item.properties && item.properties[CONFIG.productionSpeed.propertyKey])
    );
    if (realItems.length === 0) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: existingItem.key, quantity: 0 }),
      });
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [CONFIG.productionSpeed.attributeKey]: '' } }),
      });
      currentSpeedSelection = 'standard';
      return;
    }

    // Recalculate — swap variant tier if subtotal changed
    const cfg = CONFIG.productionSpeed;
    const multiplier = speedType === 'rush' ? cfg.rushMultiplier : cfg.expressMultiplier;
    const minimum = speedType === 'rush' ? (cfg.rushMinimum || 0) : (cfg.expressMinimum || 0);
    const variants = speedType === 'rush' ? cfg.rushVariants : cfg.expressVariants;
    const surcharge = Math.max(minimum, Math.round(subtotal * multiplier));
    const bestVariant = findBestVariant(variants, surcharge);

    if (bestVariant && existingItem.variant_id !== bestVariant.id) {
      log('Speed surcharge changed, swapping variant tier');
      speedProcessing = true;
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: existingItem.key, quantity: 0 }),
        });
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: bestVariant.id,
              quantity: 1,
              properties: {
                [cfg.propertyKey]: speedType,
                _hidden: 'true',
              },
            }],
          }),
        });
        await refreshCartSections();
      } finally {
        speedProcessing = false;
      }
    }
  }

  /**
   * Render the production speed selector widget into all containers.
   */
  function renderSpeedSelector(cart) {
    if (!CONFIG.productionSpeed.enabled) return;

    const containers = document.querySelectorAll('[data-production-speed-widget]');
    if (containers.length === 0) return;

    const subtotal = getCartSubtotalExcludingFees(cart);
    const cfg = CONFIG.productionSpeed;
    const rushTarget = Math.max(cfg.rushMinimum || 0, Math.round(subtotal * cfg.rushMultiplier));
    const expressTarget = Math.max(cfg.expressMinimum || 0, Math.round(subtotal * cfg.expressMultiplier));
    // Show the actual variant price (what gets charged), not the raw percentage
    const rushVariant = findBestVariant(cfg.rushVariants, rushTarget);
    const expressVariant = findBestVariant(cfg.expressVariants, expressTarget);
    const rushPrice = rushVariant ? rushVariant.price : rushTarget;
    const expressPrice = expressVariant ? expressVariant.price : expressTarget;

    function formatMoney(cents) {
      const d = cents / 100;
      return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
    }

    const tiers = cfg.tiers;
    const selected = currentSpeedSelection;

    // Collapsed-row summary (mobile bottom sheet — RESPONSIVE.md §3). Shows the
    // currently-selected tier; CSS hides it at ≥750px and shows the cards instead.
    const selectedTier = tiers[selected] || tiers.standard;
    const selectedPriceText = selected === 'rush' ? '+' + formatMoney(rushPrice)
      : selected === 'express' ? '+' + formatMoney(expressPrice)
      : 'Free';
    const lightningSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M215.79,118.17a8,8,0,0,0-5-5.66L153.18,90.9l14.66-73.33a8,8,0,0,0-13.69-7l-112,120a8,8,0,0,0,3,13l57.63,21.61L88.16,238.43a8,8,0,0,0,13.69,7l112-120A8,8,0,0,0,215.79,118.17Z"/></svg>';

    const html =
      '<div class="speed-selector">' +
        '<button type="button" class="speed-selector__collapsed" aria-expanded="false" aria-label="Production speed: ' + selectedTier.label + ' — tap to change">' +
          '<span class="speed-selector__collapsed-icon" aria-hidden="true">' + lightningSvg + '</span>' +
          '<span class="speed-selector__collapsed-title">Production Speed</span>' +
          '<span class="speed-selector__collapsed-value">' + selectedTier.label + ' &middot; ' + selectedPriceText + '</span>' +
          '<span class="speed-selector__collapsed-change">Change' +
            '<svg class="speed-selector__caret" xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"/></svg>' +
          '</span>' +
        '</button>' +
        '<div class="speed-selector__heading">' +
          lightningSvg +
          ' Production Speed <span class="speed-selector__note">&middot; How fast do you need it?</span>' +
        '</div>' +
        '<div class="speed-selector__cards" role="radiogroup" aria-label="Production speed">' +
          buildSpeedCard('standard', tiers.standard, 'Included', selected === 'standard') +
          buildSpeedCard('rush', tiers.rush, '+' + formatMoney(rushPrice), selected === 'rush') +
          buildSpeedCard('express', tiers.express, '+' + formatMoney(expressPrice), selected === 'express') +
        '</div>' +
      '</div>';

    containers.forEach(container => {
      container.innerHTML = html;

      // Collapsed-row toggle (mobile) — expand/collapse the picker in place. The
      // cards never leave the DOM, so the radio handlers below stay wired.
      const collapsedBtn = container.querySelector('.speed-selector__collapsed');
      const selectorEl = container.querySelector('.speed-selector');
      if (collapsedBtn && selectorEl) {
        collapsedBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const isExpanded = selectorEl.classList.toggle('speed-selector--expanded');
          collapsedBtn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        });
      }

      // Attach click handlers
      container.querySelectorAll('.speed-card').forEach(card => {
        card.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const speed = card.dataset.speed;
          if (speed === currentSpeedSelection || speedProcessing) return;

          // Visual feedback immediately
          container.querySelectorAll('.speed-card').forEach(c => {
            c.classList.remove('speed-card--selected');
            c.setAttribute('aria-checked', 'false');
          });
          card.classList.add('speed-card--selected');
          card.setAttribute('aria-checked', 'true');
          card.classList.add('speed-card--loading');

          // Collapse the mobile accordion back to the single row after selecting.
          if (selectorEl) selectorEl.classList.remove('speed-selector--expanded');
          if (collapsedBtn) collapsedBtn.setAttribute('aria-expanded', 'false');

          const freshCart = await getCart();
          await applySpeedSelection(speed, freshCart);
        });
      });
    });

    // Update surcharge row in order summary with actual variant price
    updateSpeedSurchargeRow(selected, selected === 'rush' ? rushPrice : selected === 'express' ? expressPrice : 0);
  }

  function buildSpeedCard(speed, tier, priceText, isSelected) {
    return '<button class="speed-card speed-card--' + speed +
      (isSelected ? ' speed-card--selected' : '') +
      '" data-speed="' + speed + '" type="button"' +
      ' role="radio" aria-checked="' + (isSelected ? 'true' : 'false') + '"' +
      ' aria-label="' + tier.label + ' — ' + tier.timeline + ', ' + priceText + '">' +
        '<div class="speed-card__radio" aria-hidden="true"></div>' +
        '<span class="speed-card__label">' +
          (tier.fire ? '<span class="speed-card__fire">' + tier.fire + '</span> ' : '') +
          tier.label +
        '</span>' +
        '<span class="speed-card__timeline">' + tier.timeline + '</span>' +
        '<span class="speed-card__price' +
          (speed === 'standard' ? ' speed-card__price--free' : '') +
        '">' + priceText + '</span>' +
      '</button>';
  }

  function updateSpeedSurchargeRow(speed, surchargeCents) {
    document.querySelectorAll('[data-speed-surcharge-row]').forEach(row => {
      // Reserve the row instead of display:none — toggle the --reserved class
      // (max-height/opacity) so the summary animates rather than hard-reflowing.
      if (speed === 'standard' || surchargeCents === 0) {
        row.classList.add('cart-order-summary__row--reserved');
        const label = row.querySelector('[data-speed-surcharge-label]');
        const value = row.querySelector('[data-speed-surcharge-value]');
        if (label) label.textContent = '';
        if (value) value.textContent = '';
        return;
      }
      const label = row.querySelector('[data-speed-surcharge-label]');
      const value = row.querySelector('[data-speed-surcharge-value]');
      if (label) label.textContent = (speed === 'rush' ? 'Rush' : 'Express') + ' Processing';
      if (value) {
        const d = surchargeCents / 100;
        value.textContent = '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
      }
      row.classList.remove('cart-order-summary__row--reserved');
    });
  }

  // ==========================================
  // TOAST NOTIFICATIONS
  // ==========================================

  function showNotification(title, message) {
    // Try StateManager (preferred)
    if (window.PitchPrintStateManager && typeof window.PitchPrintStateManager.emit === 'function') {
      window.PitchPrintStateManager.emit('toast:activate', {
        messageType: 'addToCartSuccess',
        customTitle: title,
        customMessage: message
      });
      return;
    }

    // Try direct toast function
    if (window.TOAST_CONFIG && typeof window.showToastMessage === 'function') {
      window.showToastMessage({
        messageType: 'addToCartSuccess',
        customMessage: message,
        customTitle: title
      });
      return;
    }

    // Fallback: dispatch event
    document.dispatchEvent(new CustomEvent('gift:notification', {
      detail: { title, message, type: 'success' }
    }));
  }

  /**
   * Handle post-reload notifications and cart drawer opening.
   */
  function handlePostReload() {
    // Open cart drawer if flagged
    const shouldOpenCart = sessionStorage.getItem('cart-offer-open-cart');
    if (shouldOpenCart === 'true') {
      sessionStorage.removeItem('cart-offer-open-cart');
      setTimeout(() => {
        const miniCart = document.querySelector('mini-cart');
        if (miniCart && typeof miniCart.open === 'function') {
          miniCart.open();
          log('Cart drawer opened after reload');
        }
      }, 1000);
    }

    // Show toast if flagged
    const shouldShowToast = sessionStorage.getItem('cart-offer-show-toast');
    if (shouldShowToast === 'true') {
      const title = sessionStorage.getItem('cart-offer-toast-title') || 'Free Gift Added!';
      const message = sessionStorage.getItem('cart-offer-toast-message') || 'Your free gift has been added!';
      const delay = parseInt(sessionStorage.getItem('cart-offer-toast-delay') || '1500', 10);

      sessionStorage.removeItem('cart-offer-show-toast');
      sessionStorage.removeItem('cart-offer-toast-title');
      sessionStorage.removeItem('cart-offer-toast-message');
      sessionStorage.removeItem('cart-offer-toast-delay');

      setTimeout(() => showNotification(title, message), delay);
    }
  }

  // ==========================================
  // OFFER EVALUATION
  // ==========================================

  function findUpgradeVariant(item, product, upgradeMap, optionName) {
    if (!product || !product.variants) return null;

    // product.options from /products/{handle}.js is [{name, position, values}, ...]
    const optionIndex = product.options.findIndex(o =>
      typeof o === 'string' ? o === optionName : o.name === optionName
    );
    if (optionIndex === -1) return null;

    // Cart items don't have variant_options — look up current variant from product data
    const currentVariant = product.variants.find(v => v.id === item.variant_id);
    if (!currentVariant) return null;

    const currentOptionValues = currentVariant.options || [];
    const currentValue = currentOptionValues[optionIndex];
    if (!currentValue) return null;

    const upgradeValue = upgradeMap[currentValue];
    if (!upgradeValue) return null;

    const upgradeVariant = product.variants.find(v => {
      if (!v.available) return false;
      const vOptions = v.options || [];
      return vOptions.every((val, i) => {
        if (i === optionIndex) return val === upgradeValue;
        return val === currentOptionValues[i];
      });
    });

    if (!upgradeVariant || upgradeVariant.id === item.variant_id) return null;

    const priceDiff = upgradeVariant.price - item.price;
    if (priceDiff <= 0) return null;

    return { variant: upgradeVariant, currentValue, upgradeValue, priceDiff, optionName };
  }

  async function evaluateOffers(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return { inlineOffers: new Map(), crossSells: [], bundles: [] };
    }

    const inlineOffers = new Map();
    const cartHandles = new Set();
    const cartTags = new Set();

    const uniqueHandles = [...new Set(cart.items.map(i => i.handle))];
    const products = await Promise.all(uniqueHandles.map(h => getProduct(h)));
    const productMap = new Map();
    uniqueHandles.forEach((h, i) => { if (products[i]) productMap.set(h, products[i]); });

    for (const item of cart.items) {
      if (CONFIG.excludeHandles.includes(item.handle)) continue;
      if (item.properties && item.properties[CONFIG.gift.giftPropertyKey]) continue;

      cartHandles.add(item.handle);
      const product = productMap.get(item.handle);
      if (!product) continue;

      if (product.tags) {
        product.tags.forEach(t => cartTags.add(t.toLowerCase()));
      }

      const itemOffers = [];

      const sizeUpgrade = findUpgradeVariant(item, product, CONFIG.sizeUpgrades, 'Choose Your Size');
      if (sizeUpgrade) {
        itemOffers.push({
          type: 'size_upgrade',
          label: 'Upgrade to ' + sizeUpgrade.upgradeValue,
          sublabel: 'Only $' + (sizeUpgrade.priceDiff / 100).toFixed(2) + ' more',
          item, variant: sizeUpgrade.variant, priceDiff: sizeUpgrade.priceDiff,
        });
      }

      const sidesUpgrade = findUpgradeVariant(item, product, CONFIG.sidesUpgrades, 'Choose Sides');
      if (sidesUpgrade) {
        itemOffers.push({
          type: 'sides_upgrade',
          label: 'Go Double Sided',
          sublabel: 'Only $' + (sidesUpgrade.priceDiff / 100).toFixed(2) + ' more',
          item, variant: sidesUpgrade.variant, priceDiff: sidesUpgrade.priceDiff,
        });
      }

      if (itemOffers.length > 0) {
        inlineOffers.set(item.key, itemOffers);
      }
    }

    // Cart item metadata (Liquid-injected collections + metafields)
    const metadata = getCartItemMetadata();
    const cartCollections = new Set();
    for (const [handle, meta] of metadata) {
      if (meta.collections) meta.collections.forEach(c => cartCollections.add(c));
    }
    log('Cart collections:', [...cartCollections]);

    // Bundle suggestions
    const bundles = [];
    const popsignCount = cart.items.filter(i =>
      !CONFIG.excludeHandles.includes(i.handle) &&
      !(i.properties && i.properties[CONFIG.gift.giftPropertyKey])
    ).length;

    for (const rule of CONFIG.bundleSuggestions) {
      let bundleMatch = false;
      if (rule.trigger.collection) {
        bundleMatch = cartCollections.has(rule.trigger.collection);
      } else if (rule.trigger.tag) {
        bundleMatch = cartTags.has(rule.trigger.tag.toLowerCase());
      }
      if (!bundleMatch) continue;

      for (const bundle of rule.bundles) {
        if (cartHandles.has(bundle.handle)) continue;
        if (popsignCount < bundle.minCartItems || popsignCount >= bundle.maxCartItems) continue;

        const bundleProduct = await getProduct(bundle.handle);
        if (!bundleProduct || !bundleProduct.available) continue;

        bundles.push({
          type: 'bundle', handle: bundle.handle, label: bundle.label,
          product: bundleProduct, price: bundleProduct.price, image: bundleProduct.featured_image,
        });
      }
    }

    // Cross-sells (collection-based matching via Liquid-injected metadata)
    const crossSells = [];
    const crossSellHandles = new Set();

    for (const rule of CONFIG.crossSells) {
      let hasMatch = false;
      if (rule.trigger.any) {
        hasMatch = true;
      } else if (rule.trigger.collection) {
        hasMatch = cartCollections.has(rule.trigger.collection);
      } else if (rule.trigger.optionName) {
        // Legacy fallback: match by product option name
        hasMatch = cart.items.some(item => {
          const product = productMap.get(item.handle);
          if (!product || !product.options) return false;
          return product.options.some(opt => {
            const name = typeof opt === 'string' ? opt : opt.name;
            return name === rule.trigger.optionName;
          });
        });
      } else if (rule.trigger.metafield) {
        // Match by metafield value
        const { key, value } = rule.trigger.metafield;
        hasMatch = [...metadata.values()].some(m => m[key] === value);
      }
      if (!hasMatch) continue;

      let shown = 0;
      for (const cs of rule.products) {
        if (cartHandles.has(cs.handle)) continue;
        if (crossSellHandles.has(cs.handle)) continue;
        if (shown >= rule.maxShow) break;

        const csProduct = await getProduct(cs.handle);
        if (!csProduct || !csProduct.available) continue;

        crossSells.push({
          type: 'cross_sell', handle: cs.handle, label: cs.label,
          product: csProduct, price: csProduct.price, image: csProduct.featured_image,
          isCustom: cs.isCustom || false,
          variantId: cs.variantId || null,
        });
        crossSellHandles.add(cs.handle);
        shown++;
      }
    }

    return { inlineOffers, crossSells, bundles };
  }

  // ==========================================
  // CART ACTIONS (variant swaps)
  // ==========================================

  async function swapVariant(item, newVariantId, offerType) {
    try {
      // Snapshot the current cart to preserve item order
      const cartRes = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const cart = await cartRes.json();

      // Build new items array with the swap applied in-place
      const newItems = cart.items.map(ci => {
        // Filter out null/empty properties (Shopify rejects them)
        const props = {};
        if (ci.properties) {
          for (const [k, v] of Object.entries(ci.properties)) {
            if (v !== null && v !== '') props[k] = v;
          }
        }
        if (ci.key === item.key) {
          // Tag the item so order processing knows this was an order bump
          // (e.g., "sides_upgrade" means front-only design bumped to Double Sided)
          if (offerType) props._order_bumped = offerType;
          // Swap this item's variant, keep everything else
          return { id: newVariantId, quantity: ci.quantity, properties: props };
        }
        return { id: ci.variant_id, quantity: ci.quantity, properties: props };
      });

      // Clear cart then re-add all items in original order
      // Reverse because Shopify displays newest-added first (LIFO)
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: newItems.reverse() }),
      });

      if (!addRes.ok) throw new Error('Failed to rebuild cart');

      await refreshCartSections();
      return true;
    } catch (e) {
      log('Variant swap failed:', e);
      await refreshCartSections();
      return false;
    }
  }

  async function refreshCartSections() {
    const sectionIds = ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id) sectionIds.push(mainCartItems.dataset.id);
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id) sectionIds.push(mainCartFooter.dataset.id);

    try {
      const res = await fetch(window.location.pathname + '?sections=' + sectionIds.join(','));
      const sections = await res.json();

      for (const [sectionId, html] of Object.entries(sections)) {
        const el = document.getElementById(sectionId) ||
                   document.querySelector('[data-id="' + sectionId + '"]')?.closest('.shopify-section');
        if (el) {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const newContent = parsed.querySelector('.shopify-section') || parsed.body.firstChild;
          if (newContent) {
            el.innerHTML = newContent.innerHTML;
            // Pre-seed the freshly-injected fill to the previous width (pre-paint) so the
            // subsequent updateProgressBar() glides old→new instead of snapping. (Engine path;
            // the theme's add/remove path is covered by the synchronous cartUpdate subscriber.)
            preSeedProgressFill(el);
          }
        }
      }

      // Re-init offer engine (enhanceCartItems, offers, progress bar)
      setTimeout(() => init(), 100);

      // Notify other systems (mini-cart previews, Klaviyo, etc.) that cart HTML changed
      // Clear processed items so preview system re-processes new DOM elements
      if (window.debugMiniCartPreviews && window.debugMiniCartPreviews.clearProcessed) {
        window.debugMiniCartPreviews.clearProcessed();
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      log('Section refresh failed, reloading page');
      window.location.reload();
    }
  }

  // ==========================================
  // RENDERING — Inline Offers
  // ==========================================

  function buildCalloutHTML(offer, showRationale) {
    const proof = CONFIG.socialProof[offer.type];
    const rationale = showRationale ? CONFIG.offerRationale[offer.type] : '';
    const checkSvg = '<svg class="cart-offer-callout__check-icon" viewBox="0 0 16 16" width="16" height="16">' +
      '<path d="M13.5 4.5l-7 7L3 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    return '<span class="cart-offer-callout__checkbox">' + checkSvg + '</span>' +
      '<span class="cart-offer-callout__body">' +
        '<span class="cart-offer-callout__row">' +
          '<span class="cart-offer-callout__label">' + offer.label + '</span>' +
          '<span class="cart-offer-callout__price">' + formatPriceDiff(offer.priceDiff) + '</span>' +
        '</span>' +
        (rationale ? '<span class="cart-offer-callout__rationale">' + rationale + '</span>' : '') +
      '</span>' +
      (proof ? '<span class="cart-offer-callout__badge"><svg class="cart-offer-callout__badge-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216ZM80,108a12,12,0,1,1,12,12A12,12,0,0,1,80,108Zm96,0a12,12,0,1,1-12-12A12,12,0,0,1,176,108Zm-1.07,48c-10.29,17.79-27.4,28-46.93,28s-36.63-10.2-46.92-28a8,8,0,1,1,13.84-8c7.47,12.91,19.21,20,33.08,20s25.61-7.1,33.07-20a8,8,0,0,1,13.86,8Z"/></svg><span class="cart-offer-callout__badge-text">' + proof.pct + ' ' + proof.label + '</span></span>' : '') +
      '<span class="cart-offer-callout__spinner"></span>';
  }

  function attachCalloutHandler(callout, btn, offer) {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (btn.disabled) return;

      // 1. Visual check animation
      callout.classList.add('cart-offer-callout--checked');

      // 2. Brief delay for perceived control
      await new Promise(r => setTimeout(r, 300));

      // 3. Enter loading state
      callout.classList.add('cart-offer-callout--loading');
      btn.disabled = true;

      // 4. Execute swap
      const success = await swapVariant(offer.item, offer.variant.id, offer.type);
      if (!success) {
        callout.classList.remove('cart-offer-callout--checked', 'cart-offer-callout--loading');
        btn.disabled = false;
      }
    });
  }

  function renderInlineOffers(inlineOffers) {
    document.querySelectorAll('[data-cart-offer]:not(.cart-offer-callout--compact)').forEach(el => el.remove());

    for (const [lineKey, offers] of inlineOffers) {
      const row = document.querySelector('[data-line-key="' + lineKey + '"]');
      if (!row) continue;

      const detailsCell = row.querySelector('.cart-item__details');
      if (!detailsCell) continue;

      // Show only the best offer (highest priceDiff) — sequential reveal
      const bestOffer = offers.reduce((best, o) => o.priceDiff > best.priceDiff ? o : best, offers[0]);

      const callout = document.createElement('div');
      callout.className = 'cart-offer-callout cart-offer-callout--' + bestOffer.type;
      callout.setAttribute('data-cart-offer', '');

      const btn = document.createElement('button');
      btn.className = 'cart-offer-callout__trigger';
      btn.type = 'button';
      btn.innerHTML = buildCalloutHTML(bestOffer, true);

      attachCalloutHandler(callout, btn, bestOffer);
      callout.appendChild(btn);
      detailsCell.appendChild(callout);
    }

    renderMiniCartOffers(inlineOffers);
  }

  function renderMiniCartOffers(inlineOffers) {
    document.querySelectorAll('.cart-offer-callout--compact').forEach(el => el.remove());

    // Track which drawer <li> elements are already claimed
    const claimedItems = new Set();

    for (const [lineKey, offers] of inlineOffers) {
      const firstOffer = offers[0];
      if (!firstOffer) continue;

      // Find ALL drawer items with this variant_id, pick the first unclaimed one
      const allMatches = document.querySelectorAll(
        '.mini-cart [data-variant-id="' + firstOffer.item.variant_id + '"]'
      );
      let drawerItem = null;
      for (const el of allMatches) {
        if (!claimedItems.has(el)) {
          drawerItem = el;
          break;
        }
      }
      if (!drawerItem) continue;
      claimedItems.add(drawerItem);

      // Be Yours mini-cart item details are in .product-description
      const detailsEl = drawerItem.querySelector('.product-description') ||
                        drawerItem.querySelector('.product-content');
      if (!detailsEl) continue;

      const bestOffer = offers.reduce((best, o) =>
        o.priceDiff > best.priceDiff ? o : best, offers[0]
      );

      const callout = document.createElement('div');
      callout.className = 'cart-offer-callout cart-offer-callout--compact cart-offer-callout--' + bestOffer.type;
      callout.setAttribute('data-cart-offer', '');

      const btn = document.createElement('button');
      btn.className = 'cart-offer-callout__trigger';
      btn.type = 'button';
      btn.innerHTML = buildCalloutHTML(bestOffer, false);

      attachCalloutHandler(callout, btn, bestOffer);
      callout.appendChild(btn);
      detailsEl.appendChild(callout);
    }
  }

  /**
   * Quick-add a product to cart. Custom products get _artwork_later property.
   */
  async function quickAddProduct(handle, variantId, isCustom, isPredesigned) {
    const properties = {};
    if (isPredesigned) {
      // Bundles / predesigned offers need no file upload or PitchPrint design — show the
      // "Pre-Designed" badge (--saved), NOT "Sending Artwork Later". The badge logic (Liquid
      // snippet + JS) keys off an Artwork value containing "pre-designed".
      properties['Artwork'] = 'Pre-Designed';
    } else if (isCustom) {
      properties._artwork_later = 'true';
      properties['Artwork'] = "I'll be sending this later";
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties }],
      }),
    });

    if (!res.ok) throw new Error('Failed to add product');
    showNotification('Added to Cart', 'Added to your cart!');
    await refreshCartSections();
  }

  /**
   * Detect if a product is a "custom" product (requires PitchPrint design or shape option).
   */
  function isCustomProduct(product) {
    if (!product || !product.options) return false;
    return product.options.some(opt => {
      const name = typeof opt === 'string' ? opt : opt.name;
      return name === 'Choose Your Shape' || name === 'Choose Your Design';
    });
  }

  /**
   * Build HTML for a single cross-sell/bundle offer card.
   */
  function buildOfferCardHTML(offer) {
    const imgSrc = offer.image ? (typeof offer.image === 'string' ? offer.image : offer.image) : '';
    // Bundles (and anything flagged isPredesigned) need NO artwork — they're predesigned, so
    // they must NOT be classified "custom" (which would stamp "Sending Artwork Later"). An
    // explicit isCustom:true on the offer still overrides (future custom bundle).
    const isPredesigned = offer.isPredesigned === true || (offer.type === 'bundle' && !offer.isCustom);
    const isCustom = isPredesigned
      ? false
      : (('isCustom' in offer) ? offer.isCustom : (offer.product ? isCustomProduct(offer.product) : false));
    const firstVariant = offer.product ? offer.product.variants[0] : null;
    let variantId = offer.variantId || (firstVariant ? firstVariant.id : '');
    let price = offer.price;
    let variantLabel = '';
    let variantImage = imgSrc;

    // If a specific variant is specified, use its price and image
    if (offer.variantId && offer.product) {
      const v = offer.product.variants.find(v => v.id === offer.variantId);
      if (v) {
        price = v.price;
        variantLabel = v.title || '';
        if (v.featured_image && v.featured_image.src) variantImage = v.featured_image.src;
      }
    }

    const priceFormatted = '$' + (price / 100).toFixed(2);
    const displayImage = variantImage || imgSrc;

    return '<div class="cart-offer-card cart-offer-card--' + offer.type + '">' +
      (displayImage ? '<div class="cart-offer-card__image"><img src="' + displayImage + '" alt="' + offer.label + '" loading="lazy" width="120" height="120"></div>' : '') +
      '<div class="cart-offer-card__info">' +
        '<p class="cart-offer-card__title">' + offer.label + '</p>' +
        '<p class="cart-offer-card__price">' + priceFormatted + '</p>' +
      '</div>' +
      '<button class="cart-offer-card__add-btn" type="button"' +
        ' data-handle="' + offer.handle + '"' +
        ' data-variant-id="' + variantId + '"' +
        ' data-is-custom="' + isCustom + '"' +
        ' data-predesigned="' + isPredesigned + '">' +
        'Add' +
      '</button>' +
    '</div>';
  }

  /**
   * Attach quick-add handlers to offer card buttons within a container.
   */
  function attachQuickAddHandlers(container) {
    container.querySelectorAll('.cart-offer-card__add-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (btn.classList.contains('cart-offer-card__add-btn--loading')) return;
        btn.classList.add('cart-offer-card__add-btn--loading');
        const origText = btn.textContent;

        try {
          await quickAddProduct(
            btn.dataset.handle,
            parseInt(btn.dataset.variantId, 10),
            btn.dataset.isCustom === 'true',
            btn.dataset.predesigned === 'true'
          );
          btn.textContent = '✓ Added';
          btn.classList.add('cart-offer-card__add-btn--added');
        } catch (e) {
          log('Quick add failed:', e);
          btn.textContent = origText;
          btn.classList.remove('cart-offer-card__add-btn--loading');
        }
      });
    });
  }

  function renderBottomOffers(bundles, crossSells) {
    document.querySelectorAll('.cart-offers-bottom').forEach(el => el.remove());

    const allOffers = [...bundles, ...crossSells];
    if (allOffers.length === 0) return;

    const INITIAL_SHOW = 2;
    const visibleCards = allOffers.slice(0, INITIAL_SHOW).map(o => buildOfferCardHTML(o)).join('');
    const hiddenCards = allOffers.slice(INITIAL_SHOW).map(o => buildOfferCardHTML(o)).join('');
    const showMoreBtn = hiddenCards
      ? '<button class="cart-offers-show-more" type="button" aria-expanded="false" aria-controls="cart-offers-hidden">Show more suggestions</button>'
      : '';

    function buildSection() {
      const section = document.createElement('div');
      section.className = 'cart-offers-bottom';
      section.innerHTML =
        '<h3 class="cart-offers-bottom__heading">Add a finishing touch</h3>' +
        '<div class="cart-offers-list">' + visibleCards + '</div>' +
        (hiddenCards ? '<div id="cart-offers-hidden" class="cart-offers-list cart-offers-list--hidden">' + hiddenCards + '</div>' : '') +
        showMoreBtn;

      // Show more toggle
      const btn = section.querySelector('.cart-offers-show-more');
      if (btn) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const hidden = section.querySelector('.cart-offers-list--hidden');
          if (hidden) {
            hidden.classList.remove('cart-offers-list--hidden');
            btn.setAttribute('aria-expanded', 'true');
            btn.remove();
          }
        });
      }

      attachQuickAddHandlers(section);
      return section;
    }

    // Main cart page
    const cartTable = document.querySelector('.cart-items');
    if (cartTable) {
      cartTable.parentNode.insertBefore(buildSection(), cartTable.nextSibling);
    }

    // Mini-cart cross-sell containers
    document.querySelectorAll('[data-cart-cross-sells]').forEach(container => {
      container.innerHTML = '';
      container.appendChild(buildSection());
    });
  }

  // ==========================================
  // ENHANCED CART ITEM LAYOUT (bubble badges)
  // ==========================================

  const PHOSPHOR = {
    pencil: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M227.31,73.37,182.63,28.69a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H92.69A15.86,15.86,0,0,0,104,219.31L227.31,96a16,16,0,0,0,0-22.63Z"/></svg>',
    cloudArrowUp: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="currentColor"><path d="M178.34,165.66,160,147.31V208a8,8,0,0,1-16,0V147.31l-18.34,18.35a8,8,0,0,1-11.32-11.32l32-32a8,8,0,0,1,11.32,0l32,32a8,8,0,0,1-11.32,11.32ZM160,40A88.08,88.08,0,0,0,81.29,88.68,64,64,0,1,0,72,216h40a8,8,0,0,0,0-16H72a48,48,0,0,1,0-96c1.1,0,2.2,0,3.29.12A88,88,0,0,0,72,128a8,8,0,0,0,16,0,72,72,0,1,1,100.8,66,8,8,0,0,0,3.2,15.34,7.9,7.9,0,0,0,3.2-.68A88,88,0,0,0,160,40Z"/></svg>',
    trash: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"/></svg>',
    image: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="currentColor"><path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/></svg>',
    file: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="currentColor"><path d="M213.66,82.34l-56-56A8,8,0,0,0,152,24H56A16,16,0,0,0,40,40V216a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V88A8,8,0,0,0,213.66,82.34ZM160,51.31,188.69,80H160ZM200,216H56V40h88V88a8,8,0,0,0,8,8h48V216Z"/></svg>',
    spinner: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Z" opacity="0.3"/><path d="M128,24a8,8,0,0,1,8,8V64a8,8,0,0,1-16,0V32A8,8,0,0,1,128,24Z"/></svg>',
    x: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>',
  };

  // Middle-truncate a string, preserving the end (e.g. file extension)
  function midTruncate(str, max) {
    if (!str || str.length <= max) return str;
    var keep = Math.floor((max - 1) / 2);
    return str.slice(0, keep) + '\u2026' + str.slice(str.length - keep);
  }

  function enhanceCartItems() {
    const pendingLists = document.querySelectorAll('.cart-enhanced-pending');

    if (!CONFIG.enhancedLayout) {
      // Enhanced layout disabled — remove pending class to reveal raw Liquid elements
      pendingLists.forEach(ul => ul.classList.remove('cart-enhanced-pending'));
      return;
    }

    // Mini-cart items
    document.querySelectorAll('cart-items .mini-cart__navigation > li').forEach(li => {
      if (li.dataset.autoGift === 'true') return;
      if (li.querySelector('.enhanced-bottom-row')) {
        // Liquid-first (PARITY-GAPS GAP 2): structure rendered server-side. Hydrate behaviors only.
        hydrateEnhancedItem(li);
      } else if (!li.hasAttribute('data-enhanced')) {
        // Fallback: build the enhanced layer from raw Liquid markup.
        enhanceCartItem(li);
      }
    });

    // Main cart page items (table rows)
    document.querySelectorAll('table.cart-items .cart-item').forEach(tr => {
      if (tr.querySelector('.enhanced-left-group')) {
        // Liquid-first /cart row: integrate the PitchPrint edit button into the action row the
        // same way as the drawer (quiet .enhanced-edit-btn w/ solid pencil, inserted before
        // Save-for-later). hydrateEnhancedItem's trash binding no-ops here (table remove is its
        // own column). Idempotent + re-runs on cart updates (PUB_SUB cartUpdate).
        hydrateEnhancedItem(tr);
      } else if (!tr.hasAttribute('data-enhanced')) {
        enhanceMainCartItem(tr);
      }
    });

    // All items processed — remove pending class (no longer needed)
    pendingLists.forEach(ul => ul.classList.remove('cart-enhanced-pending'));
  }

  /**
   * Integrate the PitchPrint "Edit Design" button into an enhanced left-group.
   * The pill (.pitchprint-edit-btn-pill) is injected client-side by the PitchPrint SDK
   * (mini-cart-pitchprint-previews-fixed.js), so it may not exist yet — watch for it.
   * Shared by the Liquid-first hydration path and the legacy JS build path.
   */
  function integrateEditButtonInto(li, leftGroup) {
    if (!leftGroup) return;
    function tryIntegrate() {
      if (leftGroup.querySelector('.enhanced-edit-btn')) return true;
      const editSrc = li.querySelector('.pitchprint-edit-btn-pill');
      if (!editSrc) return false;
      const editBtn = document.createElement('button');
      editBtn.className = 'enhanced-edit-btn';
      editBtn.type = 'button';
      editBtn.innerHTML = PHOSPHOR.pencil + '<span>Edit Design</span>';
      editBtn.addEventListener('click', () => editSrc.click());
      // Edit goes first in the action row (before any Save-for-later link).
      leftGroup.insertBefore(editBtn, leftGroup.firstChild);
      const editContainer = editSrc.closest('.pitchprint-edit-container') || editSrc.closest('.pitchprint-actions');
      if (editContainer) editContainer.classList.add('enhanced-hidden');
      return true;
    }
    if (tryIntegrate()) return;
    const editObserver = new MutationObserver(() => {
      if (tryIntegrate()) editObserver.disconnect();
    });
    editObserver.observe(li, { childList: true, subtree: true });
  }

  /**
   * Hydrate a Liquid-first enhanced line item (PARITY-GAPS GAP 2): the markup
   * (price/variant/badges/bottom-row + qty stepper + remove link) is already rendered
   * server-side, so we only bind behaviors. Idempotent across cart-section re-renders.
   */
  function hydrateEnhancedItem(li) {
    if (li.dataset.enhancedHydrated === 'true') return;
    li.dataset.enhancedHydrated = 'true';

    // Remove control is handled by a SINGLE delegated capture-phase listener
    // (bindRemoveDelegation), not a per-item binding. That way a click is intercepted
    // even before this hydration runs on a freshly re-rendered line (otherwise the bare
    // <a href> navigates to /cart), and removal goes through removeCartLineByKey using
    // the stable line-item key rather than a re-render-stale line index.

    // PitchPrint Edit Design button (injected async by the PP SDK).
    integrateEditButtonInto(li, li.querySelector('.enhanced-left-group'));
  }

  function enhanceCartItem(li) {
    li.setAttribute('data-enhanced', 'true');

    const desc = li.querySelector('.product-description');
    if (!desc) return;

    // 1. Move price to title row
    const productContent = desc.querySelector('.product-content');
    const discountedPrices = desc.querySelector('.product-quantity .cart-item__discounted-prices');
    const simplePrice = desc.querySelector('.product-quantity > dd.price');

    if (productContent) {
      const priceTop = document.createElement('div');
      priceTop.className = 'enhanced-price-top';
      if (discountedPrices) {
        priceTop.innerHTML = discountedPrices.innerHTML;
        discountedPrices.remove();
      } else if (simplePrice) {
        priceTop.innerHTML = simplePrice.innerHTML;
        simplePrice.remove();
      }
      productContent.appendChild(priceTop);
    }

    // 2. Convert variant options → pills, artwork/proof → status badges
    const dl = desc.querySelector('dl');
    const variantValues = [];
    const badgesRow = document.createElement('div');
    badgesRow.className = 'enhanced-status-badges';
    let hasBadges = false;

    if (dl) {
      dl.querySelectorAll('.product-option').forEach(opt => {
        if (opt.classList.contains('custom-artwork-property')) {
          hasBadges = true;
          const badge = document.createElement('span');
          const isLater = !!opt.querySelector('.send-files-later');
          const isProof = !!opt.querySelector('.custom-proof-badge');
          const ddText = (opt.querySelector('dd')?.textContent || '').trim().toLowerCase();
          const isBlank = ddText.indexOf('blank') !== -1 && ddText.indexOf('no design') !== -1;
          const isPredesigned = ddText.indexOf('pre-designed') !== -1 || ddText.indexOf('predesigned') !== -1;

          if (isLater) {
            badge.className = 'enhanced-badge enhanced-badge--later';
            badge.innerHTML = PHOSPHOR.cloudArrowUp + '<span>Sending Artwork Later</span>';
          } else if (isProof) {
            badge.className = 'enhanced-badge enhanced-badge--proof';
            const text = opt.querySelector('dd')?.textContent.trim() || 'Proof Requested';
            badge.innerHTML = PHOSPHOR.image + '<span>' + text + '</span>';
          } else if (isBlank) {
            badge.className = 'enhanced-badge enhanced-badge--blank';
            badge.innerHTML = PHOSPHOR.image + '<span>No Design (Blank)</span>';
          } else if (isPredesigned) {
            badge.className = 'enhanced-badge enhanced-badge--saved';
            badge.innerHTML = PHOSPHOR.image + '<span>Pre-Designed</span>';
          } else {
            badge.className = 'enhanced-badge enhanced-badge--saved';
            badge.innerHTML = PHOSPHOR.image + '<span>Custom Design Saved</span>';
          }
          badgesRow.appendChild(badge);
        } else {
          // Check if this is a file upload property (Front/Back with a filename)
          const dt = opt.querySelector('dt');
          const dd = opt.querySelector('dd');
          const dtText = dt ? dt.textContent.replace(/[:\s]/g, '') : '';
          if ((dtText === 'Front' || dtText === 'Back') && dd && dd.textContent.trim()) {
            hasBadges = true;
            const badge = document.createElement('span');
            badge.className = 'enhanced-badge enhanced-badge--file';
            badge.innerHTML = PHOSPHOR.file + '<span>' + dtText + ': ' + midTruncate(dd.textContent.trim(), 28) + '</span>';
            badgesRow.appendChild(badge);
          } else if (dd && dd.textContent.trim()) {
            variantValues.push(dd.textContent.trim());
          }
        }
      });

      // Variant options as a simple "/" separated text line
      if (variantValues.length) {
        const variantLine = document.createElement('div');
        variantLine.className = 'enhanced-variant-line';
        variantLine.textContent = variantValues.join(' / ');
        desc.insertBefore(variantLine, dl);
      }
      if (hasBadges) desc.insertBefore(badgesRow, dl);
      dl.classList.add('enhanced-hidden');
    }

    // 3. Build bottom row: [trash + edit] ... [quantity]
    const bottomRow = document.createElement('div');
    bottomRow.className = 'enhanced-bottom-row';

    // Left group: trash + edit wrapped together
    const leftGroup = document.createElement('div');
    leftGroup.className = 'enhanced-left-group';

    // Remove control — a bare × (close glyph), paired far-right with the qty stepper.
    // Uses cart-items AJAX updateQuantity (same as native cart-remove-button).
    let removeControl = null;
    const removeBtn = li.querySelector('cart-remove-button');
    const removeLink = li.querySelector('cart-remove-button a.delete-product');
    if (removeBtn && removeLink) {
      const trashBtn = document.createElement('button');
      trashBtn.className = 'enhanced-trash-btn';
      trashBtn.type = 'button';
      trashBtn.setAttribute('aria-label', removeLink.getAttribute('aria-label') || 'Remove item');
      trashBtn.innerHTML = PHOSPHOR.x;
      trashBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        trashBtn.classList.add('enhanced-trash-btn--loading');
        trashBtn.innerHTML = PHOSPHOR.spinner;
        // AJAX removal via cart-items custom element (same as native cart-remove-button)
        var cartItems = li.closest('cart-items');
        var lineIndex = removeBtn.dataset.index;
        if (cartItems && typeof cartItems.updateQuantity === 'function' && lineIndex) {
          cartItems.updateQuantity(lineIndex, 0);
        } else {
          // Fallback: dispatch click on the custom element (its handler calls updateQuantity)
          removeBtn.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        }
      });
      removeControl = trashBtn;

      removeBtn.classList.add('enhanced-hidden');
    }

    // Edit Design button — integrate now or watch for PitchPrint injecting it later (shared helper)
    integrateEditButtonInto(li, leftGroup);

    bottomRow.appendChild(leftGroup);

    // Right group: quantity stepper + bare × remove, paired far-right.
    const rightGroup = document.createElement('div');
    rightGroup.className = 'enhanced-right-group';
    const productQty = desc.querySelector('.product-quantity');
    if (productQty) {
      const qtyEl = productQty.querySelector('quantity-input') || productQty.querySelector('gift-quantity');
      if (qtyEl) {
        rightGroup.appendChild(qtyEl);
      }
      productQty.classList.add('enhanced-hidden');
    }
    if (removeControl) rightGroup.appendChild(removeControl);
    bottomRow.appendChild(rightGroup);

    // Insert before any order bump callout so it stays last
    const callout = desc.querySelector('[data-cart-offer]');
    if (callout) {
      desc.insertBefore(bottomRow, callout);
    } else {
      desc.appendChild(bottomRow);
    }
  }

  /**
   * Enhance a main cart page table row — mirrors mini-cart styling
   * (variant line, badge pills, purple title) without restructuring
   * the table columns (price/qty/total/remove stay in their own <td>).
   */
  function enhanceMainCartItem(tr) {
    tr.setAttribute('data-enhanced', 'true');

    const details = tr.querySelector('.cart-item__details');
    if (!details) return;

    // Purple title
    const titleLink = details.querySelector('.cart-item__name');
    if (titleLink) titleLink.classList.add('enhanced-main-title');

    // Convert variant options → variant line text + badges
    const dl = details.querySelector('dl');
    const variantValues = [];
    const badgesRow = document.createElement('div');
    badgesRow.className = 'enhanced-status-badges';
    let hasBadges = false;

    if (dl) {
      dl.querySelectorAll('.product-option').forEach(opt => {
        if (opt.classList.contains('custom-artwork-property')) {
          hasBadges = true;
          const badge = document.createElement('span');
          const isLater = !!opt.querySelector('.send-files-later');
          const isProof = !!opt.querySelector('.custom-proof-badge');
          var ddText2 = (opt.querySelector('dd') ? opt.querySelector('dd').textContent : '').trim().toLowerCase();
          var isBlank = ddText2.indexOf('blank') !== -1 && ddText2.indexOf('no design') !== -1;

          if (isLater) {
            badge.className = 'enhanced-badge enhanced-badge--later';
            badge.innerHTML = PHOSPHOR.cloudArrowUp + '<span>Sending Artwork Later</span>';
          } else if (isProof) {
            badge.className = 'enhanced-badge enhanced-badge--proof';
            var text = opt.querySelector('dd') ? opt.querySelector('dd').textContent.trim() : 'Proof Requested';
            badge.innerHTML = PHOSPHOR.image + '<span>' + text + '</span>';
          } else if (isBlank) {
            badge.className = 'enhanced-badge enhanced-badge--blank';
            badge.innerHTML = PHOSPHOR.image + '<span>No Design (Blank)</span>';
          } else {
            badge.className = 'enhanced-badge enhanced-badge--saved';
            badge.innerHTML = PHOSPHOR.image + '<span>Custom Design Saved</span>';
          }
          badgesRow.appendChild(badge);
        } else {
          var dt = opt.querySelector('dt');
          var dd = opt.querySelector('dd');
          var dtText = dt ? dt.textContent.replace(/[:\s]/g, '') : '';
          if ((dtText === 'Front' || dtText === 'Back') && dd && dd.textContent.trim()) {
            hasBadges = true;
            var badge2 = document.createElement('span');
            badge2.className = 'enhanced-badge enhanced-badge--file';
            badge2.innerHTML = PHOSPHOR.file + '<span>' + dtText + ': ' + midTruncate(dd.textContent.trim(), 28) + '</span>';
            badgesRow.appendChild(badge2);
          } else if (dd && dd.textContent.trim()) {
            variantValues.push(dd.textContent.trim());
          }
        }
      });

      // Variant line
      if (variantValues.length) {
        var variantLine = document.createElement('div');
        variantLine.className = 'enhanced-variant-line';
        variantLine.textContent = variantValues.join(' / ');
        details.insertBefore(variantLine, dl);
      }
      if (hasBadges) details.insertBefore(badgesRow, dl);
      dl.classList.add('enhanced-hidden');
    }
  }

  // ==========================================
  // MAIN INITIALIZATION
  // ==========================================

  async function init() {
    if (isEvaluating) return;
    isEvaluating = true;

    try {
      const cart = await getCart();
      if (!cart) return;

      // Seed the fill baseline at load — independent of whether the bar is in the DOM yet
      // (e.g. a product page whose drawer hasn't lazy-rendered, so updateProgressBar bails on
      // containers.length===0). Without this the FIRST cart action has no "previous" width to
      // glide from and snaps. Defaults match updateProgressBar (gift threshold 300).
      if (_prevFillPercent === null) {
        const seedGiftThreshold = CONFIG.gift.thresholds.length > 0
          ? Math.max(...CONFIG.gift.thresholds.map(t => t.amount)) : 300;
        _prevFillPercent = Math.min(100, ((getCartTotalExcludingGifts(cart) / 100) / seedGiftThreshold) * 100);
      }

      // Each step is isolated so a failure in the bar/gift code can't skip the production-speed
      // render (they share one try/catch; a swallowed throw here was dropping the speed step).
      try { updateProgressBar(cart); } catch (e) { log('progress bar error:', e); }
      try { flashChangedSummaryValues(); } catch (e) { log('summary flash error:', e); }
      try { flashChangedLinePrices(); } catch (e) { log('line price flash error:', e); }
      try { await checkGiftThreshold(cart); } catch (e) { log('gift threshold error:', e); }

      // Production speed sync + render
      if (CONFIG.productionSpeed.enabled) {
        try { await syncSpeedOnCartChange(cart); } catch (e) { log('speed sync error:', e); }
        try { renderSpeedSelector(cart); } catch (e) { log('speed render error:', e); }
      }

      if (cart.item_count === 0) {
        document.querySelectorAll('[data-cart-offer], .cart-offers-bottom')
          .forEach(el => el.remove());
        enhanceCartItems();
        return;
      }

      const { inlineOffers, crossSells, bundles } = await evaluateOffers(cart);

      log('Evaluated offers:', {
        inline: inlineOffers.size,
        bundles: bundles.length,
        crossSells: crossSells.length,
      });

      renderInlineOffers(inlineOffers);
      renderBottomOffers(bundles, crossSells);

      // Enhance cart item layout (bubble badges)
      enhanceCartItems();
    } catch (e) {
      log('Error evaluating offers:', e);
    } finally {
      isEvaluating = false;
    }
  }

  // ==========================================
  // EVENT LISTENERS
  // ==========================================

  function setupEvents() {
    // Handle post-reload toast + cart drawer
    handlePostReload();

    // Delegated remove handler (survives drawer lazy-load / re-render races).
    bindRemoveDelegation();

    // Cart update events
    const debouncedInit = () => setTimeout(init, 500);

    document.addEventListener('cart:updated', (e) => {
      if (e.detail && e.detail.source === 'cart-offer-engine') return;
      debouncedInit();
    });
    document.addEventListener('cart:refresh', debouncedInit);
    document.addEventListener('cart:item-added', debouncedInit);
    document.addEventListener('cart:quantity-changed', debouncedInit);
    // Drawer content is lazy-loaded (cart-drawer.js IntersectionObserver fetches the mini-cart
    // section, injects it, THEN fires this event). If the drawer is opened while the initial
    // init() is still in flight, debouncedInit early-returns on the isEvaluating guard and the
    // speed selector never renders (it only appeared after full page load). So render the drawer
    // widgets DIRECTLY here (not guarded), then run the normal eval.
    document.addEventListener('cartdrawer:opened', async () => {
      try {
        const cart = await getCart();
        if (cart) {
          if (CONFIG.productionSpeed.enabled) { try { renderSpeedSelector(cart); } catch (e) { log('speed render (drawer open):', e); } }
          try { updateProgressBar(cart); } catch (e) { log('bar render (drawer open):', e); }
        }
      } catch (e) { log('drawer-open widget render failed:', e); }
      debouncedInit();
    });

    // PubSub (Be Yours theme)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // Synchronous pre-seed + summary flash FIRST (pre-paint, same task as the theme's
      // section render, so they fire in sync with the value change), then the debounced re-init.
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => { preSeedProgressFill(); flashChangedSummaryValues(); flashChangedLinePrices(); });
      subscribe(PUB_SUB_EVENTS.cartUpdate, debouncedInit);
    }

    // StateManager (PitchPrint)
    if (window.PitchPrintStateManager) {
      window.PitchPrintStateManager.on('cart:updated', debouncedInit);
    }

    // Watch mini-cart visibility
    const watchMiniCart = () => {
      const miniCart = document.querySelector('mini-cart');
      if (miniCart) {
        new MutationObserver(() => {
          if (miniCart.classList.contains('is-visible') || miniCart.classList.contains('active')) {
            init();
          }
        }).observe(miniCart, { attributes: true });
      } else {
        setTimeout(watchMiniCart, 500);
      }
    };
    watchMiniCart();

    // Watch cart count bubble
    const watchCartCount = () => {
      const bubble = document.querySelector('.cart-count-bubble, #cart-icon-bubble, .header__icon--cart .icon__badge');
      if (bubble) {
        new MutationObserver(debouncedInit).observe(bubble, { childList: true, characterData: true, subtree: true });
      } else {
        setTimeout(watchCartCount, 500);
      }
    };
    watchCartCount();
  }

  // ==========================================
  // MOBILE BOTTOM-SHEET DRAG-TO-CLOSE (RESPONSIVE.md §3)
  // Drag the sheet down by its handle past a threshold to dismiss it. Uses event
  // delegation (the drawer content is lazy-loaded + re-rendered, so the handle node
  // is replaced) and the theme's own close path (cart-drawer extends MenuDrawer).
  // ==========================================
  let _sheetDragBound = false;
  function initSheetDrag() {
    if (_sheetDragBound) return;
    _sheetDragBound = true;

    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    let panel = null;        // .cart-drawer (the translated element)
    let cartDrawerEl = null; // <cart-drawer> (has closeMenuDrawer)
    let startY = 0, dy = 0, dragging = false;

    document.addEventListener('touchstart', (e) => {
      if (!isMobile()) return;
      const handle = e.target.closest && e.target.closest('.mini-cart__drag-handle');
      if (!handle) return;
      panel = document.querySelector('.cart-drawer');
      cartDrawerEl = document.querySelector('cart-drawer');
      if (!panel || !cartDrawerEl) return;
      startY = e.touches[0].clientY;
      dy = 0;
      dragging = true;
      panel.style.transition = 'none';
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      if (!dragging) return;
      dy = e.touches[0].clientY - startY;
      if (dy < 0) dy = 0; // down only
      panel.style.transform = 'translateY(' + dy + 'px)';
    }, { passive: true });

    document.addEventListener('touchend', (e) => {
      if (!dragging) return;
      dragging = false;
      const threshold = Math.min(140, (panel.offsetHeight || 600) * 0.3);
      if (dy > threshold) {
        // Animate the sheet the rest of the way down, then finalize the close.
        panel.style.transition = 'transform var(--duration-long, 0.4s) ease';
        panel.style.transform = 'translateY(100%)';
        let done = false;
        const finalize = () => {
          if (done) return;
          done = true;
          try { cartDrawerEl.closeMenuDrawer(e); } catch (err) { log('sheet close error:', err); }
          panel.style.transition = '';
          panel.style.transform = '';
        };
        panel.addEventListener('transitionend', function te(ev) {
          if (ev.propertyName !== 'transform') return;
          panel.removeEventListener('transitionend', te);
          finalize();
        });
        setTimeout(finalize, 500); // fallback if transitionend doesn't fire
      } else {
        // Snap back up.
        panel.style.transition = 'transform 0.22s var(--ps-ease-out, ease)';
        panel.style.transform = 'translateY(0)';
        setTimeout(() => { if (panel) { panel.style.transition = ''; panel.style.transform = ''; } }, 240);
      }
    }, { passive: true });
  }

  // ==========================================
  // BOOT
  // ==========================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupEvents();
      initSheetDrag();
      setTimeout(init, 300);
    });
  } else {
    setupEvents();
    initSheetDrag();
    setTimeout(init, 300);
  }

  // ==========================================
  // PUBLIC API
  // ==========================================

  window.CartOfferEngine = {
    init,
    evaluateOffers,
    CONFIG,
    setProductionSpeed: (speed) => getCart().then(cart => applySpeedSelection(speed, cart)),
    getProductionSpeed: () => currentSpeedSelection,
    // Gift threshold config API — called by the theme.liquid giftConfig (single source of truth)
    configureGifts: (settings) => {
      Object.assign(CONFIG.gift, settings);
      log('Gift config updated:', CONFIG.gift);
      lastGiftCheck = 0;
      giftProcessing = false;
      init();
    },
    manualGiftCheck: () => {
      lastGiftCheck = 0;
      giftProcessing = false;
      return init();
    },
    getGiftConfig: () => ({ ...CONFIG.gift }),
  };

  log('Cart Offer Engine loaded (unified upsell + gift system)');
})();
